import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, Tent, Car, ToggleLeft, ToggleRight, Smartphone, Building, Image as ImageIcon, MoveUp, MoveDown, Pencil, Radio,
} from "lucide-react";
import { api, peso } from "../../lib/api";
import {
  BrutalButton, BrutalCard, BrutalInput, BrutalLabel, Badge, SectionTitle,
} from "../../components/common/Brutal";

const emptyZone = { name: "", description: "", capacity: 30, type: "tent", open_for_booking: true, tent_only: false };
const emptyLivestream = { video_url: "", embed_url: "", is_live: false, updated_at: "" };

export default function AdminCMS() {
  const [zones, setZones] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [newZone, setNewZone] = useState(emptyZone);
  const [channels, setChannels] = useState([]);
  const [chForm, setChForm] = useState({ method: "gcash", account_name: "", mobile: "", account_type: "savings", account_number: "" });
  const [editingCh, setEditingCh] = useState(null);
  const [events, setEvents] = useState([]);
  const [evForm, setEvForm] = useState({ title: "", description: "", image_data: "", order: 0 });
  const [editingEv, setEditingEv] = useState(null);
  const [livestream, setLivestream] = useState(emptyLivestream);
  const [liveForm, setLiveForm] = useState("");
  const [liveBusy, setLiveBusy] = useState(false);

  const load = async () => {
    const [z, p, c, e, l] = await Promise.all([
      api.get("/zones"), api.get("/pricing"), api.get("/payment-channels"), api.get("/schedule-events"), api.get("/livestream"),
    ]);
    const nextLive = {
      video_url: l.data?.video_url || "",
      embed_url: l.data?.embed_url || "",
      is_live: !!l.data?.is_live,
      updated_at: l.data?.updated_at || "",
    };
    setZones(z.data); setPricing(p.data); setChannels(c.data); setEvents(e.data);
    setLivestream(nextLive);
    setLiveForm(nextLive.video_url);
  };

  useEffect(() => { load(); }, []);

  // ---- LIVESTREAM ----
  const syncLivestream = async (payload, successMessage) => {
    setLiveBusy(true);
    try {
      const { data } = await api.put("/livestream", payload);
      const nextLive = {
        video_url: data?.video_url || "",
        embed_url: data?.embed_url || "",
        is_live: !!data?.is_live,
        updated_at: data?.updated_at || "",
      };
      setLivestream(nextLive);
      setLiveForm(nextLive.video_url);
      toast.success(successMessage);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update livestream");
    } finally {
      setLiveBusy(false);
    }
  };
  const goLive = async (e) => {
    e.preventDefault();
    if (!liveForm.trim()) return toast.error("Livestream link required");
    await syncLivestream({ video_url: liveForm.trim(), is_live: true }, "Livestream is now live");
  };
  const endLive = async () => {
    await syncLivestream({ video_url: livestream.video_url || liveForm.trim(), is_live: false }, "Livestream ended");
  };

  // ---- ZONES ----
  const updateZone = async (z) => {
    await api.put(`/zones/${z.id}`, {
      name: z.name, description: z.description, capacity: parseInt(z.capacity, 10),
      type: z.type, open_for_booking: z.open_for_booking, tent_only: !!z.tent_only,
    });
    toast.success("Zone updated");
    load();
  };
  const toggleZone = async (z, field) => {
    const next = { ...z, [field]: !z[field] };
    await updateZone(next);
  };
  const removeZone = async (id) => {
    if (!window.confirm("Delete this zone?")) return;
    await api.delete(`/zones/${id}`); load();
  };
  const addZone = async (e) => {
    e.preventDefault();
    if (!newZone.name) return toast.error("Zone name required");
    await api.post("/zones", { ...newZone, capacity: parseInt(newZone.capacity, 10) });
    toast.success("Zone added"); setNewZone(emptyZone); load();
  };

  // ---- PRICING ----
  const updatePricing = async (p) => {
    await api.put(`/pricing/${p.id}`, {
      price_per_night: parseFloat(p.price_per_night), label: p.label,
      max_persons: parseInt(p.max_persons || 4, 10),
    });
    toast.success("Pricing updated"); load();
  };

  // ---- CHANNELS ----
  const submitChannel = async (e) => {
    e.preventDefault();
    if (!chForm.account_name) return toast.error("Account name required");
    if (chForm.method === "bank_transfer" && (!chForm.account_number || !chForm.account_type))
      return toast.error("Bank requires account number and type");
    if (chForm.method !== "bank_transfer" && !chForm.mobile) return toast.error("Mobile required");
    try {
      if (editingCh) await api.put(`/payment-channels/${editingCh}`, chForm);
      else await api.post("/payment-channels", chForm);
      toast.success("Channel saved");
      setChForm({ method: "gcash", account_name: "", mobile: "", account_type: "savings", account_number: "" });
      setEditingCh(null); load();
    } catch { toast.error("Failed"); }
  };
  const editChannel = (c) => { setEditingCh(c.id); setChForm({...c}); };
  const delChannel = async (id) => { if (!window.confirm("Delete channel?")) return; await api.delete(`/payment-channels/${id}`); load(); };

  // ---- SCHEDULE EVENTS ----
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Max 2MB");
    const r = new FileReader();
    r.onload = () => setEvForm((s) => ({ ...s, image_data: r.result }));
    r.readAsDataURL(f);
  };
  const submitEvent = async (e) => {
    e.preventDefault();
    if (!evForm.title || !evForm.image_data) return toast.error("Title and image required");
    const payload = { ...evForm, order: parseInt(evForm.order || 0, 10) };
    if (editingEv) {
      await api.put(`/schedule-events/${editingEv}`, payload);
      toast.success("Event updated");
    } else {
      await api.post("/schedule-events", payload);
      toast.success("Event added");
    }
    setEvForm({ title: "", description: "", image_data: "", order: 0 });
    setEditingEv(null);
    load();
  };
  const editEvent = (ev) => {
    setEditingEv(ev.id);
    setEvForm({ title: ev.title, description: ev.description || "", image_data: ev.image_data, order: ev.order });
    window.scrollTo({ top: document.querySelector("[data-testid='schedule-events-section']")?.offsetTop || 0, behavior: "smooth" });
  };
  const delEvent = async (id) => { if (!window.confirm("Delete this event?")) return; await api.delete(`/schedule-events/${id}`); load(); };
  const reorderEvent = async (ev, dir) => {
    const next = events.slice().sort((a,b) => a.order - b.order);
    const idx = next.findIndex((x) => x.id === ev.id);
    const j = dir === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= next.length) return;
    [next[idx].order, next[j].order] = [next[j].order, next[idx].order];
    await api.put(`/schedule-events/${ev.id}`, { title: ev.title, description: ev.description || "", image_data: ev.image_data, order: next[idx].order });
    await api.put(`/schedule-events/${next[j].id}`, { title: next[j].title, description: next[j].description || "", image_data: next[j].image_data, order: next[j].order });
    load();
  };

  return (
    <div className="space-y-8" data-testid="admin-cms">
      <SectionTitle kicker="Content Management" title="Zones, pricing, payments & events" />

      {/* LIVESTREAM */}
      <BrutalCard className="p-5" data-testid="livestream-section">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h3 className="display text-2xl flex items-center gap-2">
              <Radio className="h-5 w-5 text-[#E5247C]" />
              Livestream
            </h3>
            <div className="mt-2 flex items-center gap-2">
              <Badge color={livestream.is_live ? "green" : "yellow"}>
                {livestream.is_live ? "Live Now" : "Banner Mode"}
              </Badge>
              {livestream.updated_at && (
                <span className="text-xs font-medium text-[#6B4E9E]">
                  Updated {new Date(livestream.updated_at).toLocaleString()}
                </span>
              )}
            </div>
          </div>
          <div className="text-xs uppercase tracking-widest text-[#6B4E9E]">
            Standby image: <b>livestream_1.jpg</b>
          </div>
        </div>

        <form onSubmit={goLive} className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:items-end">
          <Field label="LiveStream Link">
            <div className="space-y-2">
              <BrutalInput
                value={liveForm}
                onChange={(e) => setLiveForm(e.target.value)}
                placeholder="Paste a livestream URL or iframe embed code"
                data-testid="livestream-link"
              />
              <p className="text-xs text-[#6B4E9E]">
                Facebook: you can paste either iframe embed code or the full video URL like
                {" "}
                <span className="font-semibold">facebook.com/PageName/videos/123...</span>
                {" "}
                or
                {" "}
                <span className="font-semibold">facebook.com/watch/?v=123...</span>.
                Short links like
                {" "}
                <span className="font-semibold">facebook.com/share/v/...</span>
                {" "}
                and
                {" "}
                <span className="font-semibold">fb.watch/...</span>
                {" "}
                won&apos;t work reliably for embeds.
              </p>
            </div>
          </Field>
          <BrutalButton variant="primary" type="submit" disabled={liveBusy} data-testid="livestream-go-live">
            <Radio className="h-4 w-4" /> {liveBusy ? "Saving..." : "Go Live"}
          </BrutalButton>
          <BrutalButton variant="outline" type="button" onClick={endLive} disabled={liveBusy} data-testid="livestream-end-live">
            <ToggleLeft className="h-4 w-4" /> End Live
          </BrutalButton>
        </form>
      </BrutalCard>

      {/* PRICING */}
      <BrutalCard className="p-5">
        <h3 className="display text-2xl mb-4">Pricing</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {pricing.map((p) => (
            <div key={p.id} className="kf-border bg-[#FFF4E6] p-4" data-testid={`pricing-${p.type}`}>
              <div className="flex items-center gap-2">
                {p.type === "tent" ? <Tent /> : <Car />}
                <Badge color="magenta">{p.type.toUpperCase()}</Badge>
              </div>
              <div className="display text-3xl mt-2">{peso(p.price_per_night)}</div>
              <div className="text-xs uppercase tracking-widest text-[#6B4E9E]">per night</div>
              <div className="mt-4 space-y-2">
                <Field label="Label"><BrutalInput value={p.label} onChange={(e) => setPricing(pricing.map((x) => x.id === p.id ? {...x, label: e.target.value} : x))} data-testid={`pricing-label-${p.type}`} /></Field>
                <Field label="Price (₱)"><BrutalInput type="number" value={p.price_per_night} onChange={(e) => setPricing(pricing.map((x) => x.id === p.id ? {...x, price_per_night: e.target.value} : x))} data-testid={`pricing-input-${p.type}`} /></Field>
                <Field label="Max Persons per Tent/Car"><BrutalInput type="number" min="1" max="20" value={p.max_persons || 4} onChange={(e) => setPricing(pricing.map((x) => x.id === p.id ? {...x, max_persons: e.target.value} : x))} data-testid={`pricing-maxp-${p.type}`} /></Field>
                <BrutalButton variant="primary" className="px-3 py-2 text-xs" onClick={() => updatePricing(p)} data-testid={`pricing-save-${p.type}`}>
                  <Save className="h-3 w-3" /> Save
                </BrutalButton>
              </div>
            </div>
          ))}
        </div>
      </BrutalCard>

      {/* ZONES */}
      <BrutalCard className="p-5">
        <h3 className="display text-2xl mb-4">Zones</h3>
        <div className="space-y-3">
          {zones.map((z) => (
            <div key={z.id} className="kf-border bg-white p-4" data-testid={`zone-row-${z.id}`}>
              <div className="grid md:grid-cols-12 gap-3">
                <div className="md:col-span-3"><BrutalLabel>Name</BrutalLabel><BrutalInput value={z.name} onChange={(e) => setZones(zones.map((x) => x.id === z.id ? {...x, name: e.target.value} : x))} data-testid={`zone-name-${z.id}`} /></div>
                <div className="md:col-span-3"><BrutalLabel>Description</BrutalLabel><BrutalInput value={z.description} onChange={(e) => setZones(zones.map((x) => x.id === z.id ? {...x, description: e.target.value} : x))} /></div>
                <div className="md:col-span-1"><BrutalLabel>Cap</BrutalLabel><BrutalInput type="number" value={z.capacity} onChange={(e) => setZones(zones.map((x) => x.id === z.id ? {...x, capacity: e.target.value} : x))} data-testid={`zone-capacity-${z.id}`} /></div>
                <div className="md:col-span-2">
                  <BrutalLabel>Type</BrutalLabel>
                  <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={z.type} onChange={(e) => setZones(zones.map((x) => x.id === z.id ? {...x, type: e.target.value} : x))} data-testid={`zone-type-${z.id}`}>
                    <option value="tent">Tent</option><option value="car">Car</option><option value="both">Both</option>
                  </select>
                </div>
                <div className="md:col-span-3 flex flex-col gap-1.5 justify-end">
                  <button onClick={() => toggleZone(z, "open_for_booking")} className={`kf-border px-2 py-1.5 font-bold text-[10px] uppercase tracking-widest flex items-center justify-between gap-1 ${z.open_for_booking ? "bg-[#10B981] text-white" : "bg-white"}`} data-testid={`zone-open-${z.id}`}>
                    {z.open_for_booking ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                    {z.open_for_booking ? "Open for booking" : "Closed"}
                  </button>
                  {z.type === "both" && (
                    <button onClick={() => toggleZone(z, "tent_only")} className={`kf-border px-2 py-1.5 font-bold text-[10px] uppercase tracking-widest flex items-center justify-between gap-1 ${z.tent_only ? "bg-[#FFB400]" : "bg-white"}`} data-testid={`zone-tentonly-${z.id}`}>
                      {z.tent_only ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                      {z.tent_only ? "Tent only allowed" : "Tents + Cars"}
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <div className="text-xs text-[#6B4E9E]">Occupied: <b>{z.occupied}</b> / {z.capacity}</div>
                <div className="flex gap-2">
                  <BrutalButton variant="primary" className="px-2 py-1.5 text-xs" onClick={() => updateZone(z)} data-testid={`zone-save-${z.id}`}><Save className="h-3 w-3" /> Save</BrutalButton>
                  <BrutalButton variant="dark" className="px-2 py-1.5 text-xs" onClick={() => removeZone(z.id)} data-testid={`zone-delete-${z.id}`}><Trash2 className="h-3 w-3" /></BrutalButton>
                </div>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={addZone} className="mt-5 kf-border bg-[#FFB400] p-4">
          <div className="font-bold text-sm uppercase tracking-widest mb-3">Add new zone</div>
          <div className="grid md:grid-cols-12 gap-3">
            <div className="md:col-span-3"><BrutalLabel>Name</BrutalLabel><BrutalInput value={newZone.name} onChange={(e) => setNewZone({...newZone, name: e.target.value})} data-testid="new-zone-name" /></div>
            <div className="md:col-span-5"><BrutalLabel>Description</BrutalLabel><BrutalInput value={newZone.description} onChange={(e) => setNewZone({...newZone, description: e.target.value})} /></div>
            <div className="md:col-span-2"><BrutalLabel>Capacity</BrutalLabel><BrutalInput type="number" value={newZone.capacity} onChange={(e) => setNewZone({...newZone, capacity: e.target.value})} /></div>
            <div className="md:col-span-2"><BrutalLabel>Type</BrutalLabel>
              <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={newZone.type} onChange={(e) => setNewZone({...newZone, type: e.target.value})}>
                <option value="tent">Tent</option><option value="car">Car</option><option value="both">Both</option>
              </select>
            </div>
          </div>
          <div className="mt-3"><BrutalButton variant="dark" type="submit"><Plus className="h-4 w-4" /> Add Zone</BrutalButton></div>
        </form>
      </BrutalCard>

      {/* PAYMENT CHANNELS */}
      <BrutalCard className="p-5" data-testid="payment-channels-section">
        <h3 className="display text-2xl mb-4">Payment Channels</h3>
        <form onSubmit={submitChannel} className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Field label="Method">
            <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={chForm.method} onChange={(e) => setChForm({...chForm, method: e.target.value})} data-testid="ch-method">
              <option value="gcash">GCASH</option>
              <option value="maya">MAYA</option>
              <option value="bank_transfer">BANK TRANSFER</option>
            </select>
          </Field>
          <Field label="Account Name"><BrutalInput value={chForm.account_name} onChange={(e) => setChForm({...chForm, account_name: e.target.value})} data-testid="ch-name" /></Field>
          {chForm.method !== "bank_transfer" ? (
            <Field label="Mobile Number"><BrutalInput value={chForm.mobile} onChange={(e) => setChForm({...chForm, mobile: e.target.value})} placeholder="+63 9..." data-testid="ch-mobile" /></Field>
          ) : (
            <>
              <Field label="Account Type">
                <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={chForm.account_type} onChange={(e) => setChForm({...chForm, account_type: e.target.value})} data-testid="ch-acct-type">
                  <option value="savings">SAVINGS</option><option value="checking">CHECKING</option>
                </select>
              </Field>
              <Field label="Account Number"><BrutalInput value={chForm.account_number} onChange={(e) => setChForm({...chForm, account_number: e.target.value})} data-testid="ch-acctnum" /></Field>
            </>
          )}
          <div className="flex items-end gap-2">
            <BrutalButton variant="primary" type="submit" data-testid="ch-save">{editingCh ? <><Save className="h-4 w-4" /> Update</> : <><Plus className="h-4 w-4" /> Add</>}</BrutalButton>
            {editingCh && <BrutalButton variant="outline" type="button" onClick={() => { setEditingCh(null); setChForm({ method: "gcash", account_name: "", mobile: "", account_type: "savings", account_number: "" }); }}>Cancel</BrutalButton>}
          </div>
        </form>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="channels-table">
            <thead className="bg-[#FFB400]">
              <tr>{["Method","Account Name","Mobile / Account #","Type","Action"].map((h) => <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest border-b-2 border-[#3D1E6D]">{h}</th>)}</tr>
            </thead>
            <tbody>
              {channels.map((c, i) => (
                <tr key={c.id} className={i % 2 ? "bg-[#FFF4E6]" : "bg-white"} data-testid={`channel-${c.id}`}>
                  <td className="px-3 py-2"><Badge color="magenta">{c.method.toUpperCase()}</Badge></td>
                  <td className="px-3 py-2 font-bold">{c.account_name}</td>
                  <td className="px-3 py-2 text-[#6B4E9E]">{c.mobile || c.account_number}</td>
                  <td className="px-3 py-2">{c.account_type ? c.account_type.toUpperCase() : "-"}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button onClick={() => editChannel(c)} className="kf-border bg-white p-1.5" data-testid={`ch-edit-${c.id}`}><Save className="h-3 w-3" /></button>
                      <button onClick={() => delChannel(c.id)} className="kf-border bg-[#EF4444] text-white p-1.5" data-testid={`ch-del-${c.id}`}><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {channels.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-[#6B4E9E]">No channels yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </BrutalCard>

      {/* SCHEDULE OF EVENTS CAROUSEL */}
      <BrutalCard className="p-5" data-testid="schedule-events-section">
        <h3 className="display text-2xl mb-4">{editingEv ? "Edit Event Photo" : "Schedule of Events (Homepage Carousel)"}</h3>
        <form onSubmit={submitEvent} className="grid sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2"><BrutalLabel>Title</BrutalLabel><BrutalInput value={evForm.title} onChange={(e) => setEvForm({...evForm, title: e.target.value})} data-testid="event-title" /></div>
          <div><BrutalLabel>Order</BrutalLabel><BrutalInput type="number" value={evForm.order} onChange={(e) => setEvForm({...evForm, order: e.target.value})} data-testid="event-order" /></div>
          <div className="flex gap-2 items-end">
            <label className="kf-border bg-white p-3 cursor-pointer flex items-center gap-2 flex-1" data-testid="event-upload">
              <ImageIcon className="h-4 w-4" /> <span className="text-xs font-bold">{evForm.image_data ? "Image attached" : "Upload image"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={onFile} />
            </label>
          </div>
          <div className="sm:col-span-4">
            <BrutalLabel>Description</BrutalLabel>
            <textarea
              className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5 text-base font-medium min-h-[80px]"
              value={evForm.description}
              onChange={(e) => setEvForm({...evForm, description: e.target.value})}
              placeholder="Tell campers about this event..."
              data-testid="event-description"
            />
          </div>
          <div className="sm:col-span-4">
            {evForm.image_data && <img src={evForm.image_data} alt="" className="kf-border max-h-32 mb-2" />}
            <div className="flex gap-2">
              <BrutalButton variant="primary" type="submit" data-testid="event-save">
                {editingEv ? <><Save className="h-4 w-4" /> Update Event</> : <><Plus className="h-4 w-4" /> Add Event Photo</>}
              </BrutalButton>
              {editingEv && (
                <BrutalButton variant="outline" type="button" onClick={() => { setEditingEv(null); setEvForm({ title: "", description: "", image_data: "", order: 0 }); }}>
                  Cancel
                </BrutalButton>
              )}
            </div>
          </div>
        </form>

        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {events.sort((a,b) => a.order - b.order).map((ev) => (
            <div key={ev.id} className="kf-border bg-white overflow-hidden" data-testid={`event-row-${ev.id}`}>
              <img src={ev.image_data} alt={ev.title} className="w-full h-40 object-cover" />
              <div className="p-3">
                <div className="font-bold text-sm">{ev.title}</div>
                {ev.description && <div className="text-xs text-[#6B4E9E] mt-1 line-clamp-2">{ev.description}</div>}
                <div className="text-[10px] uppercase tracking-widest text-[#6B4E9E] mt-1">Order: {ev.order}</div>
                <div className="flex gap-1 mt-2">
                  <button onClick={() => editEvent(ev)} className="kf-border bg-[#FFB400] p-1.5 flex-1" data-testid={`event-edit-${ev.id}`}><Pencil className="h-3 w-3 mx-auto" /></button>
                  <button onClick={() => reorderEvent(ev, "up")} className="kf-border bg-white p-1.5 flex-1" data-testid={`event-up-${ev.id}`}><MoveUp className="h-3 w-3 mx-auto" /></button>
                  <button onClick={() => reorderEvent(ev, "down")} className="kf-border bg-white p-1.5 flex-1" data-testid={`event-down-${ev.id}`}><MoveDown className="h-3 w-3 mx-auto" /></button>
                  <button onClick={() => delEvent(ev.id)} className="kf-border bg-[#EF4444] text-white p-1.5 flex-1" data-testid={`event-del-${ev.id}`}><Trash2 className="h-3 w-3 mx-auto" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </BrutalCard>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <BrutalLabel>{label}</BrutalLabel>
    {children}
  </div>
);
